// apps/hr/src/pages/hr/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  UserPlus,
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    onLeave: 0,
    totalSalary: 0,
    newHires: 0,
    departments: [],
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missingCollections, setMissingCollections] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const missing = [];

      // ─── 1. Employees ──────────────────────────────────────────
      let totalEmployees = 0;
      try {
        const employees = await pb.collection('employees').getFullList({
          filter: 'status = "active"',
        });
        totalEmployees = employees.length;
      } catch (err) {
        if (err.status === 404) missing.push('employees');
        console.warn('Employees collection error:', err.message);
      }

      // ─── 2. Today’s attendance ──────────────────────────────
      let presentToday = 0;
      let onLeave = 0;
      try {
        const today = new Date().toISOString().split('T')[0];
        const attendance = await pb.collection('attendance').getFullList({
          filter: `date = "${today}"`,
        });
        presentToday = attendance.filter(a => a.status === 'present').length;
        onLeave = attendance.filter(a => a.status === 'leave').length;
      } catch (err) {
        if (err.status === 404) missing.push('attendance');
        console.warn('Attendance collection error:', err.message);
      }

      // ─── 3. Recent activities (optional) ─────────────────────
      let activities = [];
      try {
        const result = await pb.collection('activities').getList(1, 5, {
          sort: '-created',
        });
        activities = result.items || [];
      } catch (err) {
        if (err.status === 404) missing.push('activities');
        console.warn('Activities collection error:', err.message);
      }

      // ─── 4. Salary (example: calculate from employees if they have salary) ───
      let totalSalary = 0;
      try {
        // If you have salary data in employees, sum it up
        // For now we'll keep 0 until you add salary field
        // const employees = await pb.collection('employees').getFullList();
        // totalSalary = employees.reduce((sum, e) => sum + (e.salary || 0), 0);
      } catch (err) {
        // ignore
      }

      setStats({
        totalEmployees,
        presentToday,
        onLeave,
        totalSalary,
        newHires: 0, // can compute from employees created this month
        departments: [],
      });
      setRecentActivities(activities);
      setMissingCollections(missing);

      if (missing.length > 0) {
        toast.warning(`Missing collections: ${missing.join(', ')}. Please create them in PocketBase.`);
      } else {
        toast.success('Dashboard loaded');
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center gap-1 text-sm">
          {trend > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
            {Math.abs(trend)}% from last month
          </span>
        </div>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-500">Overview of your HR department</p>
          {missingCollections.length > 0 && (
            <p className="text-xs text-amber-600 mt-1">
              ⚠️ Missing collections: {missingCollections.join(', ')}. Some data may be empty.
            </p>
          )}
        </div>
        <Link
          to="/employees/new"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition flex items-center gap-2 shadow-sm"
        >
          <UserPlus className="w-4 h-4" /> Add Employee
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Total Employees"
          value={stats.totalEmployees}
          color="bg-indigo-600"
          trend={5}
        />
        <StatCard
          icon={Calendar}
          title="Present Today"
          value={stats.presentToday}
          subtitle={`${stats.onLeave} on leave`}
          color="bg-green-600"
        />
        <StatCard
          icon={Clock}
          title="On Leave"
          value={stats.onLeave}
          color="bg-amber-600"
        />
        <StatCard
          icon={DollarSign}
          title="Monthly Salary"
          value={stats.totalSalary > 0 ? `₹${(stats.totalSalary / 100000).toFixed(1)}L` : 'N/A'}
          color="bg-purple-600"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-4 pb-4 border-b border-slate-100 last:border-0"
                >
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700">{activity.description}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(activity.created).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No recent activity. {missingCollections.includes('activities') && 'Create the "activities" collection to track activity.'}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/employees"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition group"
            >
              <span className="text-sm text-slate-700">View All Employees</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/attendance"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition group"
            >
              <span className="text-sm text-slate-700">Mark Attendance</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/leave-requests"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition group"
            >
              <span className="text-sm text-slate-700">Manage Leave Requests</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/reports"
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition group"
            >
              <span className="text-sm text-slate-700">Generate Reports</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;